import { faAngleUp } from "@fortawesome/pro-solid-svg-icons";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconButton } from "@aviary";
import { l } from "@shared/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import * as styles from "./ClickToScrollUp.styles";

interface Props {
  scrollingRef?: MutableRefObject<any>;
}

const ClickToScrollUp = ({ scrollingRef }: Props) => {
  const [isPastBreakpoint, setIsPassedBreakpoint] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const breakpointPX = 300;

    if (scrollingRef?.current) {
      const scrollableDrawerDiv = scrollingRef.current.parentElement;

      const handleDivScroll = () => {
        const scrolled = scrollingRef.current.parentElement?.scrollTop;

        if (scrolled > breakpointPX) {
          setIsPassedBreakpoint(true);
        } else {
          setIsPassedBreakpoint(false);
        }
      };

      scrollableDrawerDiv?.addEventListener("scroll", handleDivScroll);

      return () => {
        scrollableDrawerDiv?.removeEventListener("scroll", handleDivScroll);
      };
    } else {
      const handleWindowScroll = () => {
        const scrolled = window.scrollY;

        if (scrolled > breakpointPX) {
          setIsPassedBreakpoint(true);
        } else {
          setIsPassedBreakpoint(false);
        }
      };

      window?.addEventListener("scroll", handleWindowScroll);

      return () => {
        window?.removeEventListener("scroll", handleWindowScroll);
      };
    }
  }, [scrollingRef]);

  const iconButtonStyles = [
    styles.clickToScrollUp,
    isPastBreakpoint && styles.showClickToScrollUp,
    styles.overlay,
  ];

  const scrollToTop = useCallback(() => {
    if (scrollingRef?.current) {
      const containerElement = scrollingRef.current.parentElement;

      if (containerElement) {
        containerElement.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [scrollingRef]);

  return (
    <IconButton
      css={iconButtonStyles}
      isLight
      isIcon
      aria-label={t(l.sharedCatalog.ClickToScrollUp)}
      isCircular
      onClick={scrollToTop}
      size="large"
    >
      <FontAwesomeIcon icon={faAngleUp} size="lg" />
    </IconButton>
  );
};

export { ClickToScrollUp };
